class CartDrawer extends HTMLElement {
  constructor() {
    super()

    this.addEventListener('keyup', evt => evt.code === 'Escape' && this.close())
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this))
    this.setHeaderCartIconAccessibility()
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#section_cart-icon-bubble')
    cartLink.setAttribute('role', 'button')
    cartLink.setAttribute('aria-haspopup', 'dialog')
    cartLink.addEventListener('click', event => {
      event.preventDefault()
      this.open(cartLink)
    })
    cartLink.addEventListener('keydown', event => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault()
        this.open(cartLink)
      }
    })
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy)
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary')
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote)
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active')
    })

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer')
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close')
        trapFocus(containerToTrapFocusOn, focusElement)
      },
      { once: true },
    )

    document.body.classList.add('overflow-hidden')
  }

  close() {
    this.classList.remove('active')
    removeTrapFocus(this.activeElement)
    document.body.classList.remove('overflow-hidden')
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button')
    cartDrawerNote.setAttribute('aria-expanded', 'false')

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id)
    }

    cartDrawerNote.addEventListener('click', event => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'))
    })

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape)
  }

  renderContents(parsedState) {
    // Remover la clase is-empty del drawer y del drawer__inner
    this.classList.remove('is-empty')
    const $drawerInner = this.querySelector('.drawer__inner')
    if ($drawerInner) {
      $drawerInner.classList.remove('is-empty')
    }

    // Remover cualquier drawer__inner-empty que pueda quedar
    const $drawerInnerEmpty = this.querySelector('.drawer__inner-empty')
    if ($drawerInnerEmpty) {
      $drawerInnerEmpty.remove()
    }

    this.productId = parsedState.id

    try {
      this.getSectionsToRender().forEach(section => {
        try {
          const $sectionElement = document.getElementById(section.id)
          if (!$sectionElement) return

          const sectionHTML = parsedState.sections[section.section || section.id]
          if (!sectionHTML) return

          let elementToReplace
          let selectorToUse = section.selector

          // Si hay un selector específico, buscar el elemento en el DOM y extraer su contenido del HTML devuelto
          if (selectorToUse && $sectionElement.querySelector(selectorToUse)) {
            elementToReplace = $sectionElement.querySelector(selectorToUse)

            // Parsear el HTML devuelto y buscar el mismo selector dentro
            const parsedHTML = new DOMParser().parseFromString(sectionHTML, 'text/html')

            // Intentar encontrar el selector de diferentes maneras
            let $matchingElement = null

            // Primero buscar dentro de .shopify-section
            const $shopifySection = parsedHTML.querySelector('.shopify-section')
            if ($shopifySection) {
              $matchingElement = $shopifySection.querySelector(selectorToUse)
            }

            // Si no se encuentra, buscar directamente en el body
            if (!$matchingElement) {
              $matchingElement = parsedHTML.querySelector(selectorToUse)
            }

            // Si aún no se encuentra, buscar dentro de cart-drawer
            if (!$matchingElement) {
              const $cartDrawer = parsedHTML.querySelector('cart-drawer')
              if ($cartDrawer) {
                $matchingElement = $cartDrawer.querySelector(selectorToUse)
              }
            }

            // Si aún no se encuentra, buscar dentro de #CartDrawer
            if (!$matchingElement) {
              const $cartDrawerDiv = parsedHTML.querySelector('#CartDrawer')
              if ($cartDrawerDiv) {
                $matchingElement = $cartDrawerDiv.querySelector(selectorToUse)
              }
            }

            if ($matchingElement) {
              elementToReplace.innerHTML = $matchingElement.innerHTML
            } else {
              // Si no se encuentra el selector específico, intentar actualizar con el contenido del shopify-section
              console.warn(`No se encontró el selector ${selectorToUse} en el HTML devuelto para ${section.id}, intentando fallback`)
              const $shopifySection = parsedHTML.querySelector('.shopify-section')
              if ($shopifySection) {
                // Intentar encontrar el drawer__inner dentro del contenido completo
                const $fallbackInner = $shopifySection.querySelector('.drawer__inner')
                if ($fallbackInner) {
                  elementToReplace.innerHTML = $fallbackInner.innerHTML
                } else {
                  // Si aún no se encuentra, usar el contenido completo del shopify-section
                  elementToReplace.innerHTML = $shopifySection.innerHTML
                }
              }
            }
          } else {
            // Si no hay selector específico, actualizar el elemento completo
            elementToReplace = $sectionElement
            selectorToUse = '.shopify-section'
            const newHTML = this.getSectionInnerHTML(sectionHTML, selectorToUse)
            if (newHTML !== null && newHTML !== undefined && newHTML !== '') {
              elementToReplace.innerHTML = newHTML
            }
          }
        } catch (error) {
          console.error(`Error updating section ${section.id}:`, error)
        }
      })

      // Asegurarse de que la clase is-empty se actualice correctamente después de actualizar el contenido
      const $cartDrawerItems = this.querySelector('cart-drawer-items')
      if ($cartDrawerItems && parsedState.item_count > 0) {
        $cartDrawerItems.classList.remove('is-empty')
        this.classList.remove('is-empty')
      }
    } catch (error) {
      console.error('Error in renderContents:', error)
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this))
      this.open()
    })
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const $selectedElement = selector ? parsedHTML.querySelector(selector) : parsedHTML.body
    return $selectedElement ? $selectedElement.innerHTML : ''
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'component_cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'section_cart-icon-bubble',
        section: 'section_cart-icon-bubble',
        selector: null,
      },
    ]
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)
  }

  setActiveElement(element) {
    this.activeElement = element
  }
}

customElements.define('cart-drawer', CartDrawer)

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'component_cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'section_cart-icon-bubble',
        section: 'section_cart-icon-bubble',
        selector: null,
      },
    ]
  }
}

customElements.define('cart-drawer-items', CartDrawerItems)
